import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
// Can't be imported as named import, it breaks the app
// eslint-disable-next-line import/no-named-as-default
import bboxPolygon from '@turf/bbox-polygon';
import difference from '@turf/difference';
import { multiPolygonToLine, polygonToLine } from '@turf/polygon-to-line';
import { featureReduce } from '@turf/meta';
import flatten from '@turf/flatten';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import distance from '@turf/distance';
import { Feature, MultiPolygon, Point, Polygon } from '@turf/helpers';
import { getType } from '@turf/invariant';

import {
  INITIAL_GEOLOCATION_MAP_ZOOM,
  MAP_COORDS_PRECISION,
  MIN_MAP_EDITABLE_ZOOM_LEVEL,
} from '../../../common/utils/constants/constants';
import { COUNTRY_BOUNDARIES_LAYER_ID } from '../../components/layers/country-boundaries-transparent-layer/CountryBoundariesTransparentLayer';

export { default as createCirclePolygon } from '@turf/circle';

export const createWholeWorldViewMaskPolygon = (polygon) => {
  const bboxPoly = bboxPolygon([-180, -90, 180, 90]);
  return difference(bboxPoly, polygon);
};

export const getLocationCountryCode = (glMap, location) => {
  if (glMap.getLayer(COUNTRY_BOUNDARIES_LAYER_ID)) {
    const features = glMap.queryRenderedFeatures({
      layers: [COUNTRY_BOUNDARIES_LAYER_ID],
    });

    // queryRenderedFeature(location, { layers: [COUNTRY_BOUNDARIES_LAYER_ID] }) works unstable, better to do it manually
    const feature = features.find((feature) =>
      booleanPointInPolygon(location, feature.geometry),
    );

    return feature?.properties.iso_3166_1;
  }
};

export const executeWhenMapLoaded = (glMap: mapboxgl.Map, func) => {
  if (glMap.loaded()) {
    func();
  } else {
    let timerId: number;

    const onDataLoaded = () => {
      if (timerId) {
        clearTimeout(timerId);
      }

      timerId = window.setTimeout(() => {
        if (glMap.loaded()) {
          glMap.off('data', onDataLoaded);
          func();
        }
      }, 100);
    };
    glMap.on('data', onDataLoaded);

    // 'load' event is not triggered each time (found in useMapBoundsPolygon.ts) that's why we have to use dirty hack with "data" even
    // possibly fixed in mapbox-gl-js v.3.1.0 https://github.com/mapbox/mapbox-gl-js/blob/main/CHANGELOG.md
    // glMap.once('load', func);
  }
};

/**
 * Clips Bbox to the [[-180,-90],[180,90]] range if it's longer than 360 lng and higher than 180 lat
 * @param bbox number[] in format [minLng, minLat, maxLng, maxLat]
 */
export const normalizeBbox = (bbox: number[]): number[] => {
  let [bottomLeftLng, bottomLeftLat, topRightLng, topRightLat] = bbox;

  // "normalize" longitude to be not "longer" than 360 longitude length
  const bboxLength = topRightLng - bottomLeftLng;
  if (bboxLength > 360) {
    bottomLeftLng = -180;
    topRightLng = 180;
  }

  // "normalize" longitude to be not "longer" than 360 longitude length
  const bboxHeight = topRightLat - bottomLeftLat;
  if (bboxHeight > 180) {
    bottomLeftLat = -90;
    topRightLat = 90;
  }

  while (topRightLng < -180) {
    bottomLeftLng += 360;
    topRightLng += 360;
  }

  while (bottomLeftLng > 180) {
    bottomLeftLng -= 360;
    topRightLng -= 360;
  }

  return [bottomLeftLng, bottomLeftLat, topRightLng, topRightLat];
};

const FLOAT_COMPARISON_TOLERANCE = 0.1 ** (MAP_COORDS_PRECISION + 1);
export const checkZoomEditable = (zoom: number): boolean => {
  return (
    zoom > MIN_MAP_EDITABLE_ZOOM_LEVEL ||
    Math.abs(zoom - MIN_MAP_EDITABLE_ZOOM_LEVEL) <= FLOAT_COMPARISON_TOLERANCE
  );
};

export const checkZoomInteractive = (zoom: number): boolean => {
  return (
    zoom > INITIAL_GEOLOCATION_MAP_ZOOM ||
    Math.abs(zoom - INITIAL_GEOLOCATION_MAP_ZOOM) <= FLOAT_COMPARISON_TOLERANCE
  );
};

export const getNearestPointOnPolygon = (
  polygon: Feature<Polygon | MultiPolygon>,
  point: Feature<Point>,
) => {
  const polygonLine =
    getType(polygon) === 'Polygon'
      ? polygonToLine(polygon as Feature<Polygon>)
      : multiPolygonToLine(polygon as Feature<MultiPolygon>);
  const fc = flatten(polygonLine);

  return featureReduce(
    fc,
    (acc, line) => {
      const snapPoint = nearestPointOnLine(line, point);
      const dist = distance(snapPoint, point);

      if (dist > acc.dist) {
        return acc;
      }

      return { dist, point: snapPoint };
    },
    {
      dist: Number.POSITIVE_INFINITY,
      point: undefined,
    } as { dist: number; point: Feature<Point> | undefined },
  ).point;
};
